/**
 * Background Ticker
 *
 * A `setInterval` replacement that keeps firing at full rate while the tab is
 * hidden, minimised or behind another window.
 *
 * Why this exists
 * ---------------
 * Browsers deliberately starve background pages:
 *   - `requestAnimationFrame` stops entirely (no compositing → no callbacks).
 *   - `setInterval` / `setTimeout` on the main thread are clamped to ≥ 1 s, and
 *     after ~5 minutes hidden Chrome escalates to "intensive throttling" —
 *     ≥ 1 minute between wake-ups.
 *
 * Timers inside a *dedicated worker* are not subject to that clamping, and a
 * `postMessage` from a worker is delivered to the page as an ordinary task
 * rather than a throttled timer callback. So we run the clock in a worker and
 * do the actual work on the main thread when its message arrives.
 *
 * The worker is built from a Blob URL so there is no separate bundle entry and
 * no extra network request.
 */

const WORKER_SOURCE = `
let timer = null;
self.onmessage = (e) => {
  const data = e.data || {};
  if (data.type === 'start') {
    if (timer !== null) clearInterval(timer);
    timer = setInterval(() => self.postMessage('tick'), data.interval);
  } else if (data.type === 'stop') {
    if (timer !== null) clearInterval(timer);
    timer = null;
    self.close();
  }
};
`;

/** Stops the ticker. Safe to call more than once. */
export type StopTicker = () => void;

/**
 * Run `callback` every `intervalMs`, including while the page is hidden.
 *
 * Falls back to a plain `setInterval` when workers or Blob URLs are
 * unavailable (older Safari, locked-down CSP). In that case the callback is
 * still correct — it just fires less often while the tab is in the background.
 */
export const createBackgroundInterval = (
  intervalMs: number,
  callback: () => void
): StopTicker => {
  let stopped = false;

  // Guard the whole worker path: a restrictive CSP (`worker-src 'none'`) makes
  // `new Worker(blobUrl)` throw synchronously.
  try {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined') {
      throw new Error('Worker unavailable');
    }

    const blob = new Blob([WORKER_SOURCE], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    const worker = new Worker(blobUrl);

    worker.onmessage = () => {
      if (stopped) return;
      callback();
    };

    worker.postMessage({ type: 'start', interval: intervalMs });

    return () => {
      if (stopped) return;
      stopped = true;
      try { worker.postMessage({ type: 'stop' }); } catch { /* already gone */ }
      try { worker.terminate(); } catch { /* already gone */ }
      URL.revokeObjectURL(blobUrl);
    };
  } catch {
    const handle = setInterval(() => {
      if (stopped) return;
      callback();
    }, intervalMs);

    return () => {
      if (stopped) return;
      stopped = true;
      clearInterval(handle);
    };
  }
};

/**
 * One-shot `setTimeout` that is not throttled while the page is hidden.
 *
 * Built on the same worker clock as `createBackgroundInterval`: a hidden tab
 * clamps main-thread timers to >= 1 s (and, after ~5 minutes, to once a
 * minute), which is long enough to matter when the delay is meant to be a few
 * tens of milliseconds. Returns a cancel function.
 */
export const scheduleBackgroundTimeout = (
  delayMs: number,
  callback: () => void
): StopTicker => {
  let stop: StopTicker | null = null;
  let fired = false;

  stop = createBackgroundInterval(delayMs, () => {
    if (fired) return;
    fired = true;
    stop?.();
    callback();
  });

  return () => {
    fired = true;
    stop?.();
  };
};

/**
 * Ask the OS not to sleep while a long export is running.
 *
 * Screen wake locks are only granted to a visible document, so this is a
 * best-effort helper: it takes the lock when it can and re-takes it whenever
 * the page becomes visible again. Every failure path is non-fatal — an export
 * must never fail because a wake lock was refused.
 */
export const acquireWakeLock = (): (() => void) => {
  const nav = navigator as Navigator & {
    wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
  };

  if (!nav.wakeLock) return () => { /* not supported */ };

  let sentinel: { release: () => Promise<void> } | null = null;
  let released = false;

  const request = async () => {
    if (released || document.visibilityState !== 'visible') return;
    try {
      sentinel = await nav.wakeLock!.request('screen');
    } catch { /* denied — carry on without it */ }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') void request();
  };

  document.addEventListener('visibilitychange', onVisibility);
  void request();

  return () => {
    released = true;
    document.removeEventListener('visibilitychange', onVisibility);
    if (sentinel) {
      void sentinel.release().catch(() => { /* ignore */ });
      sentinel = null;
    }
  };
};
